import type { HTMLProps, ReactElement, ReactNode } from 'react';
import cx from 'clsx';
import IconCheckmarkRoundedFilledSmall from '@repo/monochrome-icons/icon-checkmark-rounded-filled-small';
import IconClockFilledSmall from '@repo/monochrome-icons/icon-clock-filled-small';
import IconCrossRoundedFilledSmall from '@repo/monochrome-icons/icon-cross-rounded-filled-small';
import IconMinusRoundedFilledSmall from '@repo/monochrome-icons/icon-minus-rounded-filled-small';
import IconProcessingFilledSmall from '@repo/monochrome-icons/icon-processing-filled-small';
import IconWarningRoundedFilledSmall from '@repo/monochrome-icons/icon-warning-rounded-filled-small';
import styles from './status.module.css';

export const STATUS_TYPES = [
  'inactive',
  'error',
  'in-progress',
  'validated',
  'waiting',
  'warning',
  'canceled',
] as const;

export type StatusType = (typeof STATUS_TYPES)[number];

export const isStatusType = (value: string): value is StatusType => {
  return STATUS_TYPES.some(statusType => statusType === value);
};

export interface StatusProps extends HTMLProps<HTMLSpanElement> {
  level?: StatusType;
  text: ReactNode;
  className?: string;
}

const ICONS = {
  error: <IconCrossRoundedFilledSmall />,
  inactive: null,
  'in-progress': <IconProcessingFilledSmall />,
  validated: <IconCheckmarkRoundedFilledSmall />,
  waiting: <IconClockFilledSmall />,
  warning: <IconWarningRoundedFilledSmall />,
  canceled: <IconMinusRoundedFilledSmall />,
} as const;

export function BadgeStatus({
  level = 'waiting',
  text,
  className,
  ...props
}: StatusProps): ReactElement {
  return (
    <span
      className={cx('caption', styles.status, styles[level], className)}
      data-test-badge-status=""
      data-test-level={level}
      {...props}
    >
      {Boolean(ICONS[level]) && <span className={styles.icon}>{ICONS[level]}</span>}
      {text}
    </span>
  );
}
